import React, { useEffect, useState } from 'react';
import {
  Container,
  CircularProgress,
  Typography,
} from '@mui/material';
import axios from 'axios';
import { fetchRecent, handleError, fetchForInput } from '../utils/lib';
import OwnersRecentList from '../layouts/OwnersRecentList';
import QuickInputDialog from '../components/QuickInputDialog';
import { useAppBar } from '../providers/AppBarProvider';
import { useConfig } from '../providers/ConfigProvider';
import { useNavigate } from 'react-router-dom';
import { useSnackbar } from '../providers/SnackBarProvider';
import { useTranslation } from 'react-i18next';

function MainOwners() {

  const [lastMovements, setLastMovements] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const navigate = useNavigate();
  const {updateTitle, toggleFab} = useAppBar();
  const {config, isLoadingConfig} = useConfig();
  const snackbar = useSnackbar();
  const { t } = useTranslation();

  // Estado para el diálogo de entrada rápida
  const [quickInputDialog, setQuickInputDialog] = useState({
    open: false,
    owner: null,
    field: null, // 'email' o 'phone'
    title: '',
    label: '',
    type: 'text',
    isLoading: false
  });

  // Estados para los prefijos móviles
  const [mobilePrefixes, setMobilePrefixes] = useState([]);
  const [selectedPrefix, setSelectedPrefix] = useState(null);
  const [isLoadingPrefixes, setIsLoadingPrefixes] = useState(false);

  useEffect(() => {
    toggleFab(true);
    const fetchData = async () => {
      const owners = await fetchRecent(1, 'owners');
      setLastMovements(owners);
      updateTitle('', config.vet.owner_naming_es_plural);
      setIsLoading(false);
    };
    fetchData();
  }, []);

  const onClick = (owner) => {
    navigate('/main/view-owner', { state: { id: owner.id, updateLastView: false } });
  }
  const onPetClick = (event, pet) => {
    event.stopPropagation()
    navigate('/main/view-pet', { state: { id: pet.id, updateLastView: false } });
  }

  // Función para abrir diálogo de entrada rápida para email
  const handleQuickEmailEdit = (owner) => {
    setQuickInputDialog({
      open: true,
      owner: owner,
      field: 'email',
      title: t('main.addEmail'),
      label: t('common.email'),
      type: 'email',
      isLoading: false
    });
  };

  // Función para abrir diálogo de entrada rápida para teléfono
  const handleQuickPhoneEdit = async (owner) => {
    setQuickInputDialog({
      open: true,
      owner: owner,
      field: 'phone',
      title: t('main.addPhone'),
      label: t('common.phone'),
      type: 'tel',
      isLoading: false
    });

    // Cargar prefijos móviles si no están cargados
    if (mobilePrefixes.length === 0) {
      try {
        setIsLoadingPrefixes(true);
        const ownersForInput = await fetchForInput('owners', 'owners_for_input');
        
        if (ownersForInput.regions_mobile_prefix) {
          setMobilePrefixes(ownersForInput.regions_mobile_prefix);
          
          // Establecer el prefijo por defecto si existe en la configuración
          const defaultPrefix = config?.vet?.default_mobile_country_prefix;
          if (defaultPrefix && !owner.country_code) {
            const defaultPrefixObj = ownersForInput.regions_mobile_prefix.find(
              prefix => prefix.mobile_prefix === defaultPrefix
            );
            if (defaultPrefixObj) {
              setSelectedPrefix(defaultPrefixObj);
            }
          } else if (owner.country_code) {
            // Si el propietario ya tiene un country_code, usar ese
            const ownerPrefix = ownersForInput.regions_mobile_prefix.find(
              prefix => prefix.country_code === owner.country_code
            );
            if (ownerPrefix) {
              setSelectedPrefix(ownerPrefix);
            }
          }
        }
      } catch (error) {
        handleError(error, snackbar);
      } finally {
        setIsLoadingPrefixes(false);
      }
    } else {
      // Si ya están cargados, solo establecer el prefijo del owner si lo tiene
      if (owner.country_code) {
        const ownerPrefix = mobilePrefixes.find(
          prefix => prefix.country_code === owner.country_code
        );
        if (ownerPrefix) {
          setSelectedPrefix(ownerPrefix);
        }
      } else {
        // O usar el prefijo por defecto
        const defaultPrefix = config?.vet?.default_mobile_country_prefix;
        if (defaultPrefix) {
          const defaultPrefixObj = mobilePrefixes.find(
            prefix => prefix.mobile_prefix === defaultPrefix
          );
          if (defaultPrefixObj) {
            setSelectedPrefix(defaultPrefixObj);
          }
        }
      }
    }
  };

  // Función para cerrar el diálogo
  const handleQuickInputClose = () => {
    setQuickInputDialog(prev => ({ ...prev, open: false }));
  };

  // Función para confirmar y guardar el valor
  const handleQuickInputConfirm = async (value) => {
    setQuickInputDialog(prev => ({ ...prev, isLoading: true }));
    
    try {
      const { owner, field } = quickInputDialog;
      
      // Preparar el body de la petición
      const requestBody = { [field]: value };
      
      // Si es un teléfono, agregar el country_code
      if (field === 'phone' && selectedPrefix?.country_code) {
        requestBody.country_code = selectedPrefix.country_code;
      }
      
      // Llamada a la API para actualizar el owner
      const response = await axios.put(
        `${import.meta.env.VITE_API_URL}owners`,
        requestBody,
        { 
          params: { 
            update_email: field === 'email' ? '' : undefined,
            update_phone: field === 'phone' ? '' : undefined,
            id: owner.id 
          }, 
          withCredentials: true 
        }
      );

      if (response.data?.data) {
        // Actualizar la lista local
        const updatedFields = { [field]: value };
        if (field === 'phone' && selectedPrefix?.country_code) {
          updatedFields.country_code = selectedPrefix.country_code;
        }
        
        setLastMovements(prevMovements => 
          prevMovements.map(movement => 
            movement.id === owner.id 
              ? { ...movement, ...updatedFields }
              : movement
          )
        );
        
        snackbar(field === 'email' ? t('main.emailUpdated') : t('main.phoneUpdated'));
        setQuickInputDialog(prev => ({ ...prev, open: false, isLoading: false }));
      }
    } catch (error) {
      handleError(error, snackbar);
      setQuickInputDialog(prev => ({ ...prev, isLoading: false }));
    }
  };

  if (isLoading || isLoadingConfig) return (
    <>
      <CircularProgress
        size={42}
        sx={{ position: 'absolute', top: '50%', left: '50%', marginTop: '-21px', marginLeft: '-21px' }}
      />
    </>
  );

  if (lastMovements.length == 0) return (
    <Container maxWidth="xl">
      <Typography
        variant="subtitle1"
        >
          {t('main.nothingHere')}
      </Typography>
    </Container>
  );

  return (
    <Container maxWidth="xl">
      {lastMovements.length > 0 && (
        <OwnersRecentList
          owners={lastMovements}
          onClick={onClick}
          onPetClick={onPetClick}
          onQuickEmailEdit={handleQuickEmailEdit}
          onQuickPhoneEdit={handleQuickPhoneEdit}
          isMultiUser={config.vet.multiuser > 1}
        />
      )}
      
      {/* Diálogo de entrada rápida */}
      <QuickInputDialog
        open={quickInputDialog.open}
        title={quickInputDialog.title}
        label={quickInputDialog.label}
        type={quickInputDialog.type}
        value={quickInputDialog.owner?.[quickInputDialog.field] || ''}
        onClose={handleQuickInputClose}
        onConfirm={handleQuickInputConfirm}
        isLoading={quickInputDialog.isLoading || isLoadingPrefixes}
        showPrefixSelector={quickInputDialog.field === 'phone'}
        mobilePrefixes={mobilePrefixes}
        selectedPrefix={selectedPrefix}
        onPrefixChange={setSelectedPrefix}
      />
    </Container>
  );
}

export default MainOwners;

